/**
 * =============================================================================
 * ALERT LIST ITEM COMPONENT
 * =============================================================================
 * 
 * Simplified alert card for the home page list view.
 * Shows: Alert Type, Priority, Title, Due Date
 * Does NOT show: Supporting data (shown only in expanded chat view)
 * 
 * INTERACTIONS:
 * - Line 120: Click handler - opens alert in chat view
 *   API: None (local state change)
 */

import { Box, Stack, Text, Badge, Group } from '@mantine/core';
import {
    Bell,
    Clock,
    Package,
    CurrencyInr,
    ChartLineUp,
    Truck,
    CheckCircle,
    Warning,
    CaretRight,
    ClockCounterClockwise,
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { memo, useMemo } from 'react';
import type { AlertItem } from './types';
import { Priority, AlertType, AlertStatus } from './types';

interface AlertListItemProps {
    alert: AlertItem;
    index: number;
    onClick: () => void;
    isHovered: boolean;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
}

// =============================================================================
// CONFIGURATION MAPS
// =============================================================================

const priorityConfig: Record<Priority, { color: string; bgColor: string; label: string }> = {
    [Priority.URGENT]: { color: 'text-halt-800', bgColor: 'bg-halt-400/30', label: 'Urgent' },
    [Priority.HIGH]: { color: 'text-orange-800', bgColor: 'bg-orange-100', label: 'High' },
    [Priority.MEDIUM]: { color: 'text-mustard-900', bgColor: 'bg-yellow-50', label: 'Medium' },
    [Priority.LOW]: { color: 'text-gray-600', bgColor: 'bg-gray-100', label: 'Low' },
};

const alertTypeIcons: Record<AlertType, React.ReactNode> = {
    [AlertType.LOW_STOCK]: <Package size={14} weight="fill" />,
    [AlertType.PO_APPROVAL]: <CheckCircle size={14} weight="fill" />,
    [AlertType.GRN_PENDING]: <Truck size={14} weight="fill" />,
    [AlertType.FORECAST_REVIEW]: <ChartLineUp size={14} weight="fill" />,
    [AlertType.PAYMENT_DUE]: <CurrencyInr size={14} weight="fill" />,
    [AlertType.PERFORMANCE_REVIEW]: <ChartLineUp size={14} weight="fill" />,
    [AlertType.STOCK_EXPIRY]: <Warning size={14} weight="fill" />,
    [AlertType.ORDER_DELAY]: <Clock size={14} weight="fill" />,
    [AlertType.CUSTOM]: <Bell size={14} weight="fill" />,
};

// =============================================================================
// COMPONENT
// =============================================================================

const AlertListItem: React.FC<AlertListItemProps> = memo(({
    alert,
    index,
    onClick,
    isHovered,
    onMouseEnter,
    onMouseLeave,
}) => {
    const priority = priorityConfig[alert.priority];
    const alertIcon = alertTypeIcons[alert.alertType] || alertTypeIcons[AlertType.CUSTOM];

    // Format due date (alertActiveUntil) with full date and time
    const formattedDueDate = useMemo(() => {
        if (!alert.dueDate) return null;
        const date = new Date(alert.dueDate);
        return date.toLocaleDateString('en-IN', { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }, [alert.dueDate]);

    // Check if overdue
    const isOverdue = useMemo(() => {
        if (!alert.dueDate || alert.status === AlertStatus.COMPLETED) return false;
        return new Date(alert.dueDate) < new Date();
    }, [alert.dueDate, alert.status]);

    // Format snoozed until
    const snoozedUntilText = useMemo(() => {
        if (!alert.snoozedUntil) return null;
        const date = new Date(alert.snoozedUntil);
        return date.toLocaleDateString('en-IN', { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }, [alert.snoozedUntil]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ delay: index * 0.03, duration: 0.2 }}
        >
            {/* ================================================================
                CLICK HANDLER - Line 120
                Opens alert in chat view (no API call, local state change)
                ================================================================ */}
            <Box
                className={`group relative cursor-pointer rounded-xl border overflow-hidden transition-all duration-200 ${
                    isHovered 
                        ? 'border-blu-400 shadow-lg scale-[1.01]' 
                        : 'border-gray-200/60 shadow-sm'
                } bg-gradient-to-br from-white via-white to-gray-50/50 backdrop-blur-sm`}
                style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 50%, rgba(248,250,252,0.9) 100%)',
                    boxShadow: isHovered 
                        ? '0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)' 
                        : '0 2px 8px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.6)',
                }}
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {/* Priority indicator */}
                <Box
                    className={`absolute left-0 top-0 h-full w-1 ${
                        alert.priority === Priority.URGENT ? 'bg-halt-800' :
                        alert.priority === Priority.HIGH ? 'bg-orange-800' :
                        alert.priority === Priority.MEDIUM ? 'bg-mustard-800' : 'bg-gray-400'
                    }`}
                />

                <Stack className="gap-2 p-3 pl-4">
                    {/* Header: Alert Type + Priority */}
                    <Group className="justify-between items-center">
                        <Group className="gap-2">
                            {/* Alert Type Badge */}
                            <Badge
                                size="xs"
                                variant="light"
                                color="blue"
                                leftSection={alertIcon}
                                className="font-medium"
                            >
                                {alert.alertTypeLabel}
                            </Badge>
                            
                            {/* Priority Badge */}
                            <Badge
                                size="xs"
                                variant="light"
                                className={`${priority.bgColor} ${priority.color} font-semibold`}
                            >
                                {priority.label}
                            </Badge>
                        </Group>

                        {/* Due Date (alertActiveUntil) or Snoozed Until */}
                        {alert.status === AlertStatus.SNOOZED && snoozedUntilText ? (
                            <Group className="gap-1 text-purple">
                                <ClockCounterClockwise size={12} weight="fill" />
                                <Text className="text-xs font-medium">Until {snoozedUntilText}</Text>
                            </Group>
                        ) : formattedDueDate ? (
                            <Group className="gap-1.5">
                                <Clock size={12} weight="fill" className={isOverdue ? 'text-halt-800' : 'text-gray-400'} />
                                {isOverdue ? (
                                    <Text className="text-xs text-halt-800 font-bold uppercase tracking-wide">
                                        Overdue
                                    </Text>
                                ) : (
                                    <Text className="text-xs text-gray-500 font-medium">
                                        {formattedDueDate}
                                    </Text>
                                )}
                            </Group>
                        ) : null}
                    </Group>

                    {/* Title */}
                    <Group className="justify-between items-center">
                        <Text className="text-sm font-semibold text-gray-900 leading-snug flex-1 pr-2">
                            {alert.title}
                        </Text>

                        {/* Arrow indicator */}
                        <CaretRight
                            size={16}
                            weight="bold"
                            className={`text-blu-800 transition-all duration-200 ${
                                isHovered ? 'translate-x-0 opacity-100' : '-translate-x-2 opacity-0'
                            }`}
                        />
                    </Group>
                </Stack>
            </Box>
        </motion.div>
    );
});

AlertListItem.displayName = 'AlertListItem';

export default AlertListItem;
